import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { get, STORES } from '../utils/indexedDB';
import type { QueueRow } from '../sync/types';
import {
  failedOpToEditForm,
  resubmitCorrectedDraft,
  type CorrectablePayload,
  type DraftEditForm,
} from '../sync/failedOpRecovery';

// Helper to normalize date string to YYYY-MM-DD format
function normalizeDateString(dateStr: string): string {
  if (!dateStr) return '';
  if (dateStr.includes('T')) return dateStr.split('T')[0];
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }
  return dateStr;
}

function formatDateDisplay(dateStr: string): string {
  const normalized = normalizeDateString(dateStr);
  if (!normalized) return 'Unknown Date';
  const date = new Date(normalized + 'T00:00:00');
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Fix a check-in that failed to sync, and resubmit it (#2082).
 *
 * Reached only from Sync Issues, at /edit-visit/draft/:queueRowId. The visit has
 * NO server CallLog — it never got that far — so everything shown here is
 * rebuilt from the failed queue row, and saving replaces that row with a
 * corrected one rather than PATCHing anything.
 *
 * This page also carried a second mode keyed on a calllogId, for editing an
 * existing visit. It had no route (App.tsx wires only the draft path) and so
 * could never run, but it held a complete second implementation of the
 * signed-visit edit rule — which read as authoritative and misled the #3446
 * design write-up until #3525 tried to route to it and hit a 404. Editing a real
 * visit belongs to ManualCallEntry, and the signed-visit rule now lives in
 * apps/projects/signed_visit.py where the server enforces it. That branch is
 * gone (#3528).
 */
export default function EditVisitPage() {
  const { queueRowId } = useParams<{ queueRowId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/sync-issues';

  const [draftForm, setDraftForm] = useState<DraftEditForm | null>(null);
  const [storeName, setStoreName] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [timeIn, setTimeIn] = useState('');
  const [driveStartOdo, setDriveStartOdo] = useState('');
  const [driveEndOdo, setDriveEndOdo] = useState('');
  const [visitNotes, setVisitNotes] = useState('');

  // Seed the form from the failed queue row. No server fetch — there is nothing
  // on the server to fetch.
  useEffect(() => {
    async function loadDraft() {
      const id = parseInt(queueRowId as string, 10);
      if (Number.isNaN(id)) {
        setError('Invalid sync item');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const row = await get<QueueRow<CorrectablePayload>>(STORES.VISIT_SYNC_QUEUE, id);
        if (!row || row.status !== 'failed') {
          setError('This sync item is no longer available to fix.');
          setIsLoading(false);
          return;
        }
        const form = failedOpToEditForm(row);
        setDraftForm(form);
        setTimeIn(form.timeIn);
        setDriveStartOdo(form.driveStartOdo);
        setDriveEndOdo(form.driveEndOdo);
        setVisitNotes(form.notes);
        setStoreName(form.storeName);
      } catch (err) {
        console.error('Failed to load sync item:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sync item');
      } finally {
        setIsLoading(false);
      }
    }
    loadDraft();
  }, [queueRowId]);

  const mileagePreview = useMemo(() => {
    if (!driveStartOdo || !driveEndOdo) return null;
    const start = parseInt(driveStartOdo, 10);
    const end = parseInt(driveEndOdo, 10);
    if (isNaN(start) || isNaN(end) || end < start) return null;
    return end - start;
  }, [driveStartOdo, driveEndOdo]);

  /**
   * Rebuild the check-in from the corrected fields and resubmit it, replacing
   * the failed row. Re-runs the write-path guards (sanitizeDriveData) so the
   * corrected op cannot reproduce the rejection that sent the rep here.
   */
  const handleSave = useCallback(async () => {
    if (!draftForm) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await resubmitCorrectedDraft({
        ...draftForm,
        timeIn,
        driveStartOdo,
        driveEndOdo,
        notes: visitNotes,
      });
      setSuccessMessage('Visit resubmitted — it will sync shortly.');
      setTimeout(() => navigate(returnTo), 1500);
    } catch (err) {
      console.error('Failed to resubmit visit:', err);
      setError(err instanceof Error ? err.message : 'Failed to resubmit visit');
    } finally {
      setIsSaving(false);
    }
  }, [draftForm, timeIn, driveStartOdo, driveEndOdo, visitNotes, navigate, returnTo]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Loading visit data...</p>
        </div>
      </div>
    );
  }

  if (error && !draftForm) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="mb-2 text-lg font-semibold text-slate-800">Unable to Load Visit</h2>
          <p className="mb-6 text-slate-600">{error}</p>
          <button
            onClick={() => navigate(returnTo)}
            className="w-full rounded-lg bg-primary-600 py-3 font-medium text-white hover:bg-primary-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!draftForm) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(returnTo)}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="font-heading text-lg font-semibold text-slate-800">Fix Visit</h1>
              <p className="text-sm text-slate-500">{storeName}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-slate-700">
              {formatDateDisplay(draftForm.dteCall ?? '')}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="mx-4 mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {successMessage && (
        <div className="mx-4 mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">{successMessage}</div>
      )}

      {/* Why this check-in failed, and what to fix. */}
      {draftForm.failedReason && (
        <div className="mx-4 mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="font-semibold text-amber-800">This visit couldn&rsquo;t be saved</h3>
          </div>
          <p className="mt-1 text-sm text-amber-700">{draftForm.failedReason}</p>
          <p className="mt-2 text-sm text-amber-700">
            Correct the highlighted fields below and tap Resubmit to try again.
          </p>
        </div>
      )}

      <div className="p-4 space-y-4">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Time In <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              value={timeIn}
              onChange={(e) => setTimeIn(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Drive-to Mileage
              <span className="ml-2 text-xs font-normal text-slate-400">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input
                  type="number"
                  inputMode="numeric"
                  value={driveStartOdo}
                  onChange={(e) => setDriveStartOdo(e.target.value)}
                  placeholder="Start"
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400"
                />
                <span className="mt-1 block text-xs text-slate-500">Start Odometer</span>
              </div>
              <div>
                <input
                  type="number"
                  inputMode="numeric"
                  value={driveEndOdo}
                  onChange={(e) => setDriveEndOdo(e.target.value)}
                  placeholder="End"
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400"
                />
                <span className="mt-1 block text-xs text-slate-500">End Odometer</span>
              </div>
            </div>
            {mileagePreview !== null && (
              <div className="mt-2 rounded-lg bg-blue-50 p-2 text-center">
                <span className="text-sm text-blue-600">
                  Miles: <span className="font-semibold">{mileagePreview}</span>
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Notes</label>
            <textarea
              value={visitNotes}
              onChange={(e) => setVisitNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900"
            />
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full rounded-lg bg-primary-600 py-3 font-medium text-white hover:bg-primary-700 disabled:bg-slate-300"
        >
          {isSaving ? 'Resubmitting...' : 'Resubmit'}
        </button>
      </div>
    </div>
  );
}
